"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_SETTINGS_TITLE_COLUMN = exports.APP_SETTINGS_CONTAINER_TABLE = void 0;
exports.appSettingsChildTableKeys = appSettingsChildTableKeys;
exports.parseAppSettingsDatabaseBlockIds = parseAppSettingsDatabaseBlockIds;
exports.appSettingsColumnSpecs = appSettingsColumnSpecs;
exports.appSettingsRowCells = appSettingsRowCells;
exports.appSettingsRowFromCells = appSettingsRowFromCells;
exports.planAppSettingsChanges = planAppSettingsChanges;
exports.splitAppSettingsGenerations = splitAppSettingsGenerations;
exports.appSettingsCellExcesses = appSettingsCellExcesses;
exports.isCompiledAppSettingsTable = isCompiledAppSettingsTable;
exports.isCompiledAppSettingsCell = isCompiledAppSettingsCell;
exports.isAppSettingsTableOfApp = isAppSettingsTableOfApp;
exports.appDefinitionSettingsCellExcess = appDefinitionSettingsCellExcess;
const database_identity_1 = require("@streamlain/database-identity");
const app_base_config_1 = require("./app-base-config");
const app_settings_engine_1 = require("./app-settings-engine");
const app_settings_tables_1 = require("./app-settings-tables");
const app_stored_files_1 = require("./app-stored-files");
/**
 * How the rows of the Config tables are stored as StreamLain tables: column
 * specs, the cells of a row, a stored row read back and the writes between
 * two states. Server writer and frontend store share this one mapping.
 */
/** Table of the container row; its database is the App Container's config database. */
exports.APP_SETTINGS_CONTAINER_TABLE = 'container';
/** Title column every table has. */
exports.APP_SETTINGS_TITLE_COLUMN = 'title';
/** Keys of the Config tables that get a database of their own. */
function appSettingsChildTableKeys() {
    return [...(0, app_settings_tables_1.appSettingsModel)().tables.keys()].filter(key => key !== exports.APP_SETTINGS_CONTAINER_TABLE);
}
/**
 * The block ids of an App Container's Config tables: one id for every table
 * but `container`, no other key.
 */
function parseAppSettingsDatabaseBlockIds(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new app_settings_engine_1.SettingsMappingError('settingsDatabaseBlockIds is missing');
    }
    const ids = value;
    const keys = appSettingsChildTableKeys();
    const unknown = Object.keys(ids).filter(key => !keys.includes(key));
    if (unknown.length > 0) {
        throw new app_settings_engine_1.SettingsMappingError(`settingsDatabaseBlockIds names unknown tables: ${unknown.join(', ')}`);
    }
    const seen = new Set();
    for (const key of keys) {
        const id = ids[key];
        if (typeof id !== 'string' || id === '') {
            throw new app_settings_engine_1.SettingsMappingError(`settingsDatabaseBlockIds has no block id for ${key}`);
        }
        if (seen.has(id)) {
            throw new app_settings_engine_1.SettingsMappingError(`settingsDatabaseBlockIds names block ${id} twice`);
        }
        seen.add(id);
    }
    return ids;
}
const OPTION_COLOR = database_identity_1.SELECT_OPTION_COLOR_BY_NAME.Grey;
/**
 * Columns of one Config table. Select options use their value as id, so a
 * stored cell holds the value itself. `:parent` points at the parent table
 * one way; config rows are written by the settings writers only.
 */
function appSettingsColumnSpecs(tableKey, tableBlockId) {
    return (0, app_settings_tables_1.appSettingsModel)()
        .table(tableKey)
        .columns.map(column => {
        const spec = { id: column.id, name: column.id, type: column.type };
        if (column.type === 'select' || column.type === 'multi-select') {
            return {
                ...spec,
                data: {
                    options: (column.options ?? []).map(value => ({
                        id: value,
                        value,
                        color: OPTION_COLOR,
                    })),
                },
            };
        }
        if (column.type === 'relation') {
            if (!column.target) {
                throw new app_settings_engine_1.SettingsMappingError(`Relation ${column.id} of ${tableKey} names no table`);
            }
            return {
                ...spec,
                data: {
                    targetDatabaseId: tableBlockId(column.target),
                    targetFieldId: exports.APP_SETTINGS_TITLE_COLUMN,
                    cardinality: 'one',
                },
            };
        }
        return spec;
    });
}
/**
 * The cells a table stores for one row: its title and every value it sets.
 * The title of the container row is the App Container name, which the
 * config row owns; settings never write it.
 */
function appSettingsRowCells(tableKey, row) {
    return tableKey === exports.APP_SETTINGS_CONTAINER_TABLE
        ? row.cells
        : { [exports.APP_SETTINGS_TITLE_COLUMN]: row.title, ...row.cells };
}
function isStringList(value) {
    return Array.isArray(value) && value.every(item => typeof item === 'string');
}
function storedCell(column, value) {
    switch (column.type) {
        case 'rich-text':
        case 'select':
            return typeof value === 'string' ? value : undefined;
        case 'number':
            return typeof value === 'number' && Number.isFinite(value)
                ? value
                : undefined;
        case 'checkbox':
            return typeof value === 'boolean' ? value : undefined;
        case 'multi-select':
        case 'relation':
            return isStringList(value) ? value : undefined;
    }
}
/**
 * A stored row of a Config table as a settings row. `cell` returns the
 * stored value of a column, or undefined/null when the row has none. A value
 * of another type than its column is refused.
 */
function appSettingsRowFromCells(tableKey, rowId, cell) {
    const title = tableKey === exports.APP_SETTINGS_CONTAINER_TABLE
        ? rowId
        : cell(exports.APP_SETTINGS_TITLE_COLUMN);
    if (typeof title !== 'string') {
        throw new app_settings_engine_1.SettingsMappingError(`Row ${rowId} of Config table ${tableKey} has no title`);
    }
    const cells = {};
    for (const column of (0, app_settings_tables_1.appSettingsModel)().table(tableKey).columns) {
        const value = cell(column.id);
        if (value === undefined || value === null)
            continue;
        const stored = storedCell(column, value);
        if (stored === undefined) {
            throw new app_settings_engine_1.SettingsMappingError(`Row ${rowId} of Config table ${tableKey}: ${column.id} is no ${column.type} value`);
        }
        cells[column.id] = stored;
    }
    return { id: rowId, title, cells };
}
function sameCell(a, b) {
    if (Array.isArray(a) || Array.isArray(b)) {
        return (Array.isArray(a) &&
            Array.isArray(b) &&
            a.length === b.length &&
            a.every((item, index) => item === b[index]));
    }
    return a === b;
}
/**
 * The writes that turn the stored rows into the next rows, per table: new
 * rows, rows that left and changed cells of kept rows. Tables without a
 * change are left out.
 */
function planAppSettingsChanges(stored, next) {
    const changes = new Map();
    for (const tableKey of (0, app_settings_tables_1.appSettingsModel)().tables.keys()) {
        const before = new Map((stored[tableKey] ?? []).map(row => [row.id, row]));
        const after = next[tableKey] ?? [];
        const insertRows = [];
        const updates = [];
        for (const row of after) {
            const old = before.get(row.id);
            before.delete(row.id);
            if (!old) {
                insertRows.push(row);
                continue;
            }
            const oldCells = appSettingsRowCells(tableKey, old);
            const newCells = appSettingsRowCells(tableKey, row);
            for (const [columnId, value] of Object.entries(newCells)) {
                const previous = oldCells[columnId];
                if (previous === undefined || !sameCell(previous, value)) {
                    updates.push({ rowId: row.id, columnId, value });
                }
            }
            for (const columnId of Object.keys(oldCells)) {
                if (!(columnId in newCells)) {
                    updates.push({ rowId: row.id, columnId, clear: true });
                }
            }
        }
        const deleteRowIds = [...before.keys()];
        if (insertRows.length + deleteRowIds.length + updates.length > 0) {
            changes.set(tableKey, { insertRows, deleteRowIds, updates });
        }
    }
    return changes;
}
/**
 * The rows of one generation and the rows of every other generation. The
 * container row belongs to every generation; it names the valid one.
 */
function splitAppSettingsGenerations(rows, generation) {
    const current = {};
    const stale = {};
    for (const [tableKey, tableRows] of Object.entries(rows)) {
        if (tableKey === exports.APP_SETTINGS_CONTAINER_TABLE) {
            current[tableKey] = [...tableRows];
            stale[tableKey] = [];
            continue;
        }
        current[tableKey] = tableRows.filter(row => row.cells[app_settings_engine_1.SETTINGS_GEN_COLUMN] === generation);
        stale[tableKey] = tableRows.filter(row => row.cells[app_settings_engine_1.SETTINGS_GEN_COLUMN] !== generation);
    }
    return { current, stale };
}
/** Every cell of the rows above the limit of its column type, largest share first. */
function appSettingsCellExcesses(rows) {
    const excesses = [];
    for (const [tableKey, tableRows] of Object.entries(rows)) {
        const types = new Map((0, app_settings_tables_1.appSettingsModel)()
            .table(tableKey)
            .columns.map(column => [column.id, column.type]));
        for (const row of tableRows) {
            for (const [columnId, value] of Object.entries(appSettingsRowCells(tableKey, row))) {
                const type = columnId === exports.APP_SETTINGS_TITLE_COLUMN
                    ? 'title'
                    : types.get(columnId);
                const limit = type
                    ? database_identity_1.DATABASE_PRODUCT_LIMITS.cellLimits[type]
                    : undefined;
                if (!limit)
                    continue;
                const actual = (0, database_identity_1.measureDatabaseCellValue)(limit, value, `Config table ${tableKey} cell ${columnId}`);
                if (actual > limit.limit) {
                    excesses.push({
                        tableKey,
                        rowId: row.id,
                        columnId,
                        columnType: type,
                        actual,
                        limit: limit.limit,
                        unit: limit.unit,
                    });
                }
            }
        }
    }
    return excesses.sort((a, b) => b.actual / b.limit - a.actual / a.limit);
}
const COMPILED_PATH_PREFIXES = ['appBase.', 'appArtifact.'];
function isCompiledPath(path) {
    return COMPILED_PATH_PREFIXES.some(prefix => path.startsWith(prefix));
}
/**
 * Whether only the server writes a whole Config table: every path of it lies
 * in the compiled App manifest (`appBase.*`) or artifact (`appArtifact.*`).
 */
function isCompiledAppSettingsTable(tableKey) {
    const def = app_settings_tables_1.APP_SETTINGS_TABLES.find(table => table.key === tableKey);
    if (!def)
        throw new app_settings_engine_1.SettingsMappingError(`unknown Config table ${tableKey}`);
    return def.paths.every(isCompiledPath);
}
/**
 * Whether only the server writes a Config table cell: a cell of a compiled
 * table ({@link isCompiledAppSettingsTable}); in the container table the
 * column path decides.
 */
function isCompiledAppSettingsCell(tableKey, columnId) {
    if (isCompiledAppSettingsTable(tableKey))
        return true;
    return tableKey === exports.APP_SETTINGS_CONTAINER_TABLE && isCompiledPath(columnId);
}
/** Whether the rows of a Config table belong to one App (the table lies below `apps`). */
function isAppSettingsTableOfApp(tableKey) {
    for (let key = tableKey; key !== null; key = (0, app_settings_tables_1.appSettingsModel)().table(key).parent) {
        if (key === 'apps')
            return true;
    }
    return false;
}
/**
 * The largest excess of the cells an App definition takes in every App
 * Container, null when all fit. The platform stores the file bodies as
 * workspace blobs, so the measure names each file by its blob key as the
 * server stores it and leaves the body out: an App above the limit here
 * cannot be installed.
 */
function appDefinitionSettingsCellExcess(definition) {
    const stored = {
        ...definition,
        files: (0, app_stored_files_1.storedAppFiles)(definition.files),
    };
    const config = (0, app_stored_files_1.withStoredAppFiles)((0, app_base_config_1.createAppBaseAppBaseConfig)({
        name: stored.appId,
        manifestId: stored.appId,
    }));
    const rows = (0, app_settings_tables_1.appSettingsToRows)(stored.appId, { ...config, appDefinitions: { [stored.appId]: stored } }, { generation: 'measure', revision: 0 });
    return (appSettingsCellExcesses(rows).find(excess => isAppSettingsTableOfApp(excess.tableKey)) ?? null);
}
